'use client'
import React, { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { 
  Camera, 
  Video, 
  Palette, 
  Gamepad2,
  Cloud, 
  Smartphone, 
  Tv, 
  Globe,
  BarChart3,
  TrendingUp,
  Users,
  Eye
} from "lucide-react";

// Type definitions
interface TechnologyItem {
  name: string;
  category: string;
  adoption: number;
  iconColor: string;
  bgColor: string;
  borderColor: string;
}

interface TechnologyData {
  "Content Creation": TechnologyItem[];
  "Distribution": TechnologyItem[];
  "Analytics": TechnologyItem[];
}

interface IndustryTechnologyStackConfig {
  title: string;
  description: string;
  technologyData: TechnologyData;
}

interface TIndustriesConfig {
  industryTechnologyStack: IndustryTechnologyStackConfig;
}

const technologyDataIcons = {
  "Content Creation": [
    { icon: Gamepad2 },
    { icon: Palette },
    { icon: Video },
    { icon: Camera }
  ],
  "Distribution": [
    { icon: Cloud },
    { icon: Globe },
    { icon: Smartphone },
    { icon: Tv }
  ],
  "Analytics": [
    { icon: BarChart3 },
    { icon: TrendingUp },
    { icon: Users },
    { icon: Eye }
  ]
};

type SectionKey = keyof TechnologyData;



const tabContainerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      delay: 0.3,
      staggerChildren: 0.1,
    },
  },
};

const tabVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.3,
    },
  },
  hover: {
    scale: 1.05,
    transition: {
      duration: 0.2,
    },
  },
  tap: {
    scale: 0.95,
  },
};

const gridContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.3,
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2,
      staggerChildren: 0.05,
      staggerDirection: -1,
    },
  },
};


const textVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      delay: 0.2,
    },
  },
};

export default function IndustryTechnologyStack({
  config
}: {
  config: TIndustriesConfig['industryTechnologyStack']
}) {
  const [activeSection, setActiveSection] = useState<SectionKey>("Content Creation");
  const sections = Object.keys(config.technologyData) as SectionKey[];

  const handleTabClick = (section: SectionKey) => {
    setActiveSection(section);
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div
          className="text-center mb-12"
          variants={{
            hidden: { opacity: 0, y: 30 },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.6,
                ease: "easeOut",
              },
            },
          }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.h2 
            className="text-3xl lg:text-4xl font-normal text-gray-900 mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {config.title}
          </motion.h2>
          <motion.p 
            className="text-lg text-gray-600 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {config.description}
          </motion.p>
        </motion.div>

        {/* Tab Navigation */}
        <motion.div
          className="flex justify-center mb-8"
          variants={tabContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div 
            className="bg-gray-100 p-1 rounded-lg inline-flex"
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            {sections.map((section) => (
              <motion.button
                key={section}
                onClick={() => handleTabClick(section)}
                className={`px-6 py-2 text-sm font-medium rounded-md transition-all duration-200 relative ${
                  activeSection === section
                    ? "text-gray-900"
                    : "text-gray-600 hover:text-gray-900"
                }`}
                variants={tabVariants}
                whileHover="hover"
                whileTap="tap"
              >
                {activeSection === section && (
                  <motion.div
                    className="absolute inset-0 bg-white shadow-sm rounded-md"
                    layoutId="activeTab"
                    initial={false}
                    transition={{
                      type: "spring",
                      stiffness: 500,
                      damping: 30,
                    }}
                  />
                )}
                <span className="relative z-10">{section}</span>
              </motion.button>
            ))}
          </motion.div>
        </motion.div>

        {/* Technology Cards Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeSection}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            variants={gridContainerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {config.technologyData[activeSection].map((tech, index) => {
              const IconComponent = technologyDataIcons[activeSection][index].icon;
              return (
                <motion.div
                  key={`${activeSection}-${index}`}
                  className={`${tech.bgColor} ${tech.borderColor} border rounded-xl p-6 hover:shadow-lg transition-shadow duration-300 relative overflow-hidden`}
                  variants={{
                    hidden: { 
                      opacity: 0, 
                      y: 40,
                      scale: 0.9,
                    },
                    visible: {
                      opacity: 1,
                      y: 0,
                      scale: 1,
                      transition: {
                        duration: 0.5,
                        ease: "easeOut",
                      },
                    },
                    exit: {
                      opacity: 0,
                      y: -20,
                      scale: 0.9,
                      transition: {
                        duration: 0.2,
                      },
                    },
                    hover: {
                      y: -8,
                      scale: 1.02,
                      transition: {
                        duration: 0.2,
                        ease: "easeOut",
                      },
                    },
                  }}
                  whileHover="hover"
                  layout 
                >
                  {/* Background decoration */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  />

                  {/* Icon and Name */}
                  <div className="text-center mb-4 relative z-10">
                    <motion.div 
                      className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mx-auto mb-3 shadow-sm"
                      variants={ {
                        hidden: { 
                          scale: 0, 
                          rotate: -180,
                        },
                        visible: {
                          scale: 1,
                          rotate: 0,
                          transition: {
                            type: "spring",
                            stiffness: 200,
                            damping: 15,
                            delay: 0.1,
                          },
                        },
                        hover: {
                          rotate: 360,
                          scale: 1.1,
                          transition: {
                            duration: 0.4,
                            ease: "easeInOut",
                          },
                        },
                      }}
                      whileHover="hover"
                    >
                      <IconComponent className={`w-6 h-6 ${tech.iconColor}`} />
                    </motion.div>
                    <motion.h3 
                      className="text-lg font-semibold text-gray-900 mb-1"
                      variants={textVariants}
                    >
                      {tech.name}
                    </motion.h3>
                    <motion.p 
                      className="text-sm text-gray-600"
                      variants={textVariants}
                    >
                      {tech.category}
                    </motion.p>
                  </div>

                  {/* Adoption Rate */}
                  <motion.div 
                    className="space-y-2 relative z-10"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    <div className="flex justify-between items-center">
                      <motion.span 
                        className="text-sm text-gray-700 font-medium"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 }}
                      >
                        Industry Adoption
                      </motion.span>
                      <motion.span 
                        className="text-sm font-bold text-gray-900"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ 
                          delay: 0.6,
                          type: "spring",
                          stiffness: 200,
                        }}
                      >
                        {tech.adoption}%
                      </motion.span>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <motion.div
                        className="bg-gray-800 h-2 rounded-full"
                        variants={{
                          hidden: { width: 0 },
                          visible: (adoption: number) => ({
                            width: `${adoption}%`,
                            transition: {
                              duration: 1,
                              delay: 0.5,
                              ease: "easeOut",
                            },
                          }),
                        }}
                        initial="hidden"
                        animate="visible"
                        custom={tech.adoption}
                      />
                    </div>
                  </motion.div>
                </motion.div>
              );
            })}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}